/* eslint-disable max-lines */
import * as React from 'react';
import { Link, useLocation, useHistory } from 'react-router-dom';
import { Button, Select, useDeviceType } from '@cian/ui-kit';

import { IUser } from '../../types/user';
import { declineNumeric } from '../../../app/helpers/format_helper';
import { RequestDemandButton } from '../../containers/realtor_demand/request_demand_button';
import { isRealtorsForum } from './utils/is_realtors_forum';
import { showAuthorBlog } from './utils/show_author_blog';
import { DemandBannerLocation } from '../../containers/realtor_demand/contants/demand_banner_location';

import s from './list_header.css';

interface IGroup {
  regex: RegExp;
  name: string;
  orderingType: string;
}

const groups: IGroup[] = [
  {
    regex: /.*/gi,
    name: 'Новое',
    orderingType: '-date_publish',
  },
  {
    regex: /.*/gi,
    name: 'Популярное',
    orderingType: '-num_views',
  },
  {
    regex: /.*/gi,
    name: 'Обсуждаемое',
    orderingType: '-comments_count',
  },
  {
    regex: /blog/gi,
    name: 'Понравившееся',
    orderingType: '-likes_count',
  },
];

const ROUTE_HEADERS = new Map([
  ['/magazine', 'Журнал о недвижимости'],
  ['/magazine/', 'Журнал о недвижимости'],
  ['/magazine-arenda', 'Аренда недвижимости'],
  ['/magazine-arenda/', 'Аренда недвижимости'],
  ['/magazine-novostrojki-stati', 'Новостройки'],
  ['/magazine-novostrojki-stati/', 'Новостройки'],
  ['/magazine-vtorichnaya', 'Вторичная недвижимость'],
  ['/magazine-vtorichnaya/', 'Вторичная недвижимость'],
  ['/magazine-ipoteka-stati', 'Ипотека'],
  ['/magazine-ipoteka-stati/', 'Ипотека'],
  ['/magazine-zagorodnaya', 'Загородная недвижимость'],
  ['/magazine-zagorodnaya/', 'Загородная недвижимость'],
  ['/magazine-stroim-dom', 'Строим дом'],
  ['/magazine-stroim-dom/', 'Строим дом'],
  ['/magazine-kommercheskaya', 'Коммерческая недвижимость'],
  ['/magazine-kommercheskaya/', 'Коммерческая недвижимость'],
  ['/magazine-pomoshnik-po-nedvizhimosti', 'Гайды'],
  ['/magazine-pomoshnik-po-nedvizhimosti/', 'Гайды'],
  ['/magazine-biblioteka-dokumentov', 'Шаблоны документов'],
  ['/magazine-biblioteka-dokumentov/', 'Шаблоны документов'],
  ['/magazine-dizajn-i-remont', 'Дизайн и ремонт'],
  ['/magazine-dizajn-i-remont/', 'Дизайн и ремонт'],
  ['/magazine-gorod', 'Жизнь города'],
  ['/magazine-gorod/', 'Жизнь города'],
  ['/magazine-o-redakcii-cianzhurnala', 'Редакция'],
  ['/magazine-o-redakcii-cianzhurnala/', 'Редакция'],
  ['/magazine-kalendar-sobytij', 'Календарь событий'],
  ['/magazine-kalendar-sobytij/', 'Календарь событий'],
  ['/novosti', 'Новости о недвижимости'],
  ['/novosti/', 'Новости о недвижимости'],
  ['/novosti/market', 'Новости рынка недвижимости'],
  ['/novosti/market/', 'Новости рынка недвижимости'],
  ['/novosti/company', 'Новости компании'],
  ['/novosti/company/', 'Новости компании'],
  ['/stati', 'Статьи о недвижимости'],
  ['/stati/', 'Статьи о недвижимости'],
  ['/stati/interview', 'Интервью со специалистами рынка недвижимости'],
  ['/stati/interview/', 'Интервью со специалистами рынка недвижимости'],
  ['/stati/review', 'Обзор новостроек'],
  ['/stati/review/', 'Обзор новостроек'],
  ['/stati/analytics', 'Аналитика рынка недвижимости'],
  ['/stati/analytics/', 'Аналитика рынка недвижимости'],
  ['/moi-blog', 'Мой Блог'],
  ['/moi-blog/', 'Мой Блог'],
  ['/blogs', 'Блоги'],
  ['/blogs/', 'Блоги'],
  ['/blogs-specialist', 'Блоги'],
  ['/blogs-specialist/', 'Блоги'],
  ['/blogs-tendentsiy-rynka', 'Блоги'],
  ['/blogs-tendentsiy-rynka/', 'Блоги'],
  ['/forum-rieltorov', 'Вопросы риелтору'],
  ['/forum-rieltorov/', 'Вопросы риелтору'],
  ['/forum-rieltorov-tendentsiy-rynka', 'Вопросы риелтору'],
  ['/forum-rieltorov-tendentsiy-rynka/', 'Вопросы риелтору'],
  ['/forum-rieltorov-gorodskaya-nedvizhimost', 'Вопросы риелтору'],
  ['/forum-rieltorov-gorodskaya-nedvizhimost/', 'Вопросы риелтору'],
  ['/forum-rieltorov-kommercheskaya-nedvizhimost', 'Вопросы риелтору'],
  ['/forum-rieltorov-kommercheskaya-nedvizhimost/', 'Вопросы риелтору'],
  ['/forum-rieltorov-zagorodnaya-nedvizhimost', 'Вопросы риелтору'],
  ['/forum-rieltorov-zagorodnaya-nedvizhimost/', 'Вопросы риелтору'],
  ['/forum-rieltorov-zarubezhnaya-nedvizhimost', 'Вопросы риелтору'],
  ['/forum-rieltorov-zarubezhnaya-nedvizhimost/', 'Вопросы риелтору'],
  ['/forum-rieltorov-ipoteka', 'Вопросы риелтору'],
  ['/forum-rieltorov-ipoteka/', 'Вопросы риелтору'],
  ['/forum-rieltorov-specialist', 'Вопросы риелтору'],
  ['/forum-rieltorov-specialist/', 'Вопросы риелтору'],
]);

interface IListHeader {
  author?: string;
  pathname: string;
  ordering: string;
  searchTag?: string;
  recordsCount: number;
  refreshScroll?(): void;
  currentUser: IUser;
  authorId?: string;
  specialistLink?: string;
  listType: string;
  authorType: string;
  search?: string;
  isBlockedPage: boolean;
}

const FILTER_VALUES = [
  { value: '/magazine', label: 'Все материалы' },
  { value: '/stati', label: 'Статьи' },
  { value: '/novosti', label: 'Новости' },
  { value: '/blogs', label: 'Блоги' },
  { value: '/forum-rieltorov', label: 'Вопросы риелтору' },
];

const authorTypeHeader = (type: string, authorType: string) => {
  const value = type.toLowerCase() + '-' + authorType;
  switch (value) {
    case 'blog-author_id':
      return 'Блог ведет';

    case 'question-author_id':
      return 'Вопросы';

    case 'question-comment_user_id':
      return 'Советы от';

    default:
      return '';
  }
};

export const ListHeader: React.FC<IListHeader> = props => {
  const location = useLocation();
  const browserHistory = useHistory();
  const deviceType = useDeviceType();

  const { pathname, authorId, listType } = props;
  const { ordering, refreshScroll, currentUser } = props;
  let header: any = ROUTE_HEADERS.get(pathname);

  if (props.author) {
    header = (
      <span>
        <span className={s['list-header__text_title-auxiliary-text']}>
          {authorTypeHeader(props.listType, props.authorType)}:
        </span>
        {props.specialistLink ? (
          <Link to={`${props.specialistLink}`} className={s['list-header__text_title-link-author']}>
            {' '}
            {props.author}
          </Link>
        ) : (
          <span> {props.author}</span>
        )}
      </span>
    );
  } else if (props.searchTag) {
    header = `#${props.searchTag}`;
  } else if (props.search) {
    header = props.search;
  }

  const onFilterChange = (value: string | number | string[] | React.ReactText[], refreshScroll: any) => {
    if (refreshScroll) {
      refreshScroll();
    }

    Object.assign(location, { pathname: value });
    const query = new URLSearchParams(location.search);
    query.set('page', '1');
    location.search = query.toString();

    browserHistory.push(location);
  };

  const showFilters = (pathname: string, recordsCount: number, refreshScroll: any) => {
    return (
      <div>
        <div className={`${s['search-container']}`}>
          <Select
            inline={true}
            value={pathname.replace(/\/+$/, '')}
            onChange={value => onFilterChange(value, refreshScroll)}
            options={FILTER_VALUES}
          />
          <span className={s['search-results']}>
            {`${recordsCount} ${declineNumeric(recordsCount, ['результат', 'результата', 'результатов'])}`}
          </span>
        </div>
      </div>
    );
  };

  const FIRST_PAGE = '1';

  return (
    <div
      className={`
          ${s['list-header']}
          ${props.searchTag || props.search ? s['list-header-tag'] : ''}
          ${isRealtorsForum(pathname) && !currentUser.isBlock ? s['list-header__ask-question'] : ''}
          ${showAuthorBlog(pathname, authorId) ? s['list-header__blog-author'] : ''}
          ${listType === 'Blog' || listType === 'Question' ? s['list-header-blog-question'] : ''}
          ${props.isBlockedPage ? s['list-header_mobile-hidden'] : ''}
        `}
    >
      <div className={s['list-header__text']}>
        <h1 className={s['list-header__text_title']}>{header}</h1>
        {(!!props.searchTag || !!props.search) && showFilters(pathname, props.recordsCount, refreshScroll)}
      </div>
      {(props.searchTag || props.search) && (
        <div className={s['list-header-mobile__text']}>
          <span className={s['list-header-mobile__text-tag']}>{header}</span>
          {(!!props.searchTag || !!props.search) && showFilters(pathname, props.recordsCount, refreshScroll)}
        </div>
      )}
      <div
        className={`
            ${isRealtorsForum(pathname) && !currentUser.isBlock ? s['list-header__filters-wrap'] : ''}
            ${showAuthorBlog(pathname, authorId) ? s['list-header__filters-wrap-subscribe'] : ''}
          `}
      >
        {isRealtorsForum(pathname) && !currentUser.isBlock && (
          <div className={s['list-header__actions']}>
            <Link to="/add-vopros" className={s['list-header__ask-question_btn']}>
              <span className={s['list-header__ask-question_icon']} />
              <span>Задать вопрос</span>
            </Link>
            <RequestDemandButton
              bannerLocation={DemandBannerLocation.REALTOR_DEMAND_BUTTON}
              renderButton={({ onClick }) => (
                <Button
                  theme="fill_secondary"
                  size={deviceType === 'phone' ? 'M' : 'XS'}
                  fullWidth={true}
                  onClick={onClick}
                >
                  Подобрать риелтора
                </Button>
              )}
            />
          </div>
        )}
        {/*TODO добавить подписку в этапе 2.4-2.6
          showAuthorBlog(pathname, authorId) &&
          <div className={s['list-header__subscribe-wrap']}>
            <span className={s['list-header__subscribe-icon']} />
            <span className={s['list-header__subscribe-text']}>Подписаться</span>
          </div>*/}
        {!props.isBlockedPage && (
          <ul className={s['list-header__nav']}>
            {groups.map(({ regex, name, orderingType }: IGroup, i: number) => {
              const isActive = ordering === orderingType;

              if (!pathname.match(regex)) {
                return <div key={`list-header-${i}`} />;
              }

              return (
                <li key={`list-header-${i}`} className={s['list-header__nav-item']}>
                  <div
                    className={`${s['list-header__nav-link']} ${isActive ? s['list-header__nav-link_active'] : ''}`}
                    onClick={() => {
                      const query = new URLSearchParams(location.search);
                      query.set('page', FIRST_PAGE);
                      query.set('ordering', orderingType);

                      if (props.searchTag) {
                        query.set('tag', props.searchTag as string);
                      }

                      if (props.search) {
                        query.set('search', props.search as string);
                      }

                      location.search = query.toString();
                      browserHistory.push(location);
                    }}
                  >
                    {name}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};
